import { createContext, forwardRef, useCallback, useContext, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { AlertTriangle, Check, ChevronLeft, ChevronRight, Info, Loader2, X } from 'lucide-react';
import { ApiError } from '../lib/api';
import { humanize } from '../lib/format';

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

/* ------------------------------------------------------------------ buttons */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'quiet';
const variants: Record<Variant, string> = {
  primary: 'bg-basil text-white hover:bg-basil-dark border border-basil-dark/20 shadow-[inset_0_-1px_0_rgb(0_0_0/0.12)]',
  secondary: 'bg-surface text-ink border border-line-strong hover:border-ink-3 hover:bg-sunken/50',
  ghost: 'text-ink-2 hover:bg-sunken hover:text-ink border border-transparent',
  danger: 'bg-surface text-chili border border-chili/40 hover:bg-chili-tint',
  quiet: 'text-basil hover:underline underline-offset-2 border border-transparent px-0!',
};
const sizes = { sm: 'h-8 px-2.5 text-[13px] gap-1.5', md: 'h-9 px-3.5 text-sm gap-2', lg: 'h-11 px-5 text-[15px] gap-2' };

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: keyof typeof sizes;
  loading?: boolean;
  icon?: ReactNode;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ variant = 'secondary', size = 'md', loading, icon, className, children, disabled, ...rest }, ref) => (
  <button
    ref={ref}
    className={cx('inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors disabled:opacity-55 disabled:cursor-not-allowed', variants[variant], sizes[size], className)}
    disabled={disabled || loading}
    {...rest}
  >
    {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
    {children}
  </button>
));

export function ButtonLink({ variant = 'secondary', size = 'md', icon, className, children, ...rest }: LinkProps & { variant?: Variant; size?: keyof typeof sizes; icon?: ReactNode }) {
  return (
    <Link className={cx('inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors', variants[variant], sizes[size], className)} {...rest}>
      {icon}
      {children}
    </Link>
  );
}

/* -------------------------------------------------------------------- forms */
export function Field({ label, hint, error, children, className, required }: { label: ReactNode; hint?: ReactNode; error?: string; children: (id: string) => ReactNode; className?: string; required?: boolean }) {
  const id = useId();
  return (
    <div className={cx('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="text-[13px] font-medium text-ink-2">
        {label}
        {required && <span className="text-chili"> *</span>}
      </label>
      {children(id)}
      {error ? (
        <p className="text-[12.5px] text-chili" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[12.5px] text-ink-3">{hint}</p>
      ) : null}
    </div>
  );
}
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...p }, ref) => <input ref={ref} className={cx('field', className)} {...p} />);
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(({ className, ...p }, ref) => <select ref={ref} className={cx('field', className)} {...p} />);
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...p }, ref) => <textarea ref={ref} className={cx('field min-h-[88px]', className)} {...p} />);

/** Shorthand: labelled text input bound to a value. */
export function TextField({ label, value, onChange, error, hint, required, className, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & { label: ReactNode; value: string | number | undefined | null; onChange: (v: string) => void; error?: string; hint?: ReactNode }) {
  return (
    <Field label={label} error={error} hint={hint} required={required} className={className}>
      {(id) => <Input id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} required={required} {...rest} />}
    </Field>
  );
}

export function Checkbox({ checked, onChange, label, description }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; description?: ReactNode }) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer select-none">
      <input type="checkbox" className="mt-0.5 size-4 accent-basil" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <span className="text-sm">{label}</span>
        {description && <span className="block text-[12.5px] text-ink-3">{description}</span>}
      </span>
    </label>
  );
}

/* ----------------------------------------------------------------- statuses */
type Tone = 'neutral' | 'waiting' | 'active' | 'good' | 'bad' | 'muted';
const tones: Record<Tone, string> = {
  neutral: 'bg-sunken text-ink-2 ring-line-strong',
  waiting: 'bg-saffron-tint text-saffron-ink ring-saffron/40',
  active: 'bg-slate-tint text-slate ring-slate/30',
  good: 'bg-basil-tint text-basil-dark ring-basil/30',
  bad: 'bg-chili-tint text-chili ring-chili/30',
  muted: 'bg-transparent text-ink-3 ring-line',
};
const dot: Record<Tone, string> = { neutral: 'bg-ink-3', waiting: 'bg-saffron', active: 'bg-slate', good: 'bg-basil', bad: 'bg-chili', muted: 'bg-line-strong' };

/** One map for every lifecycle in the product, so a status always looks the same everywhere. */
const STATUS_TONE: Record<string, Tone> = {
  DRAFT: 'muted',
  PENDING_APPROVAL: 'waiting',
  APPROVED: 'active',
  PLACED: 'waiting',
  ACCEPTED: 'active',
  PROCESSING: 'active',
  READY_FOR_DISPATCH: 'active',
  READY: 'active',
  DISPATCHED: 'active',
  DELIVERED: 'good',
  CLOSED: 'good',
  CANCELLED: 'muted',
  REJECTED: 'bad',
  SUBMITTED: 'waiting',
  PUBLISHED: 'good',
  SUSPENDED: 'bad',
  ARCHIVED: 'muted',
  OPEN: 'active',
  AWARDED: 'good',
  EXPIRED: 'muted',
  WITHDRAWN: 'muted',
  INVITED: 'waiting',
  VIEWED: 'neutral',
  QUOTED: 'active',
  DECLINED: 'muted',
  UNPAID: 'waiting',
  PENDING: 'waiting',
  PAID: 'good',
  CAPTURED: 'good',
  AUTHORIZED: 'active',
  FAILED: 'bad',
  REFUNDED: 'neutral',
  PARTIALLY_REFUNDED: 'neutral',
  ONBOARDING: 'neutral',
  PENDING_REVIEW: 'waiting',
  ACTIVE: 'good',
  CHANGES_REQUESTED: 'waiting',
  UNDER_REVIEW: 'active',
  RESOLVED_REFUND: 'good',
  RESOLVED_NO_REFUND: 'good',
  RESOLVED: 'good',
  ISSUED: 'waiting',
  VOID: 'muted',
  HELD: 'waiting',
  BATCHED: 'active',
  SUCCEEDED: 'good',
  RUNNING: 'active',
  DEAD: 'bad',
  QUEUED: 'waiting',
  SENT: 'good',
  SKIPPED: 'muted',
  WAITING: 'muted',
};
const STATUS_LABEL: Record<string, string> = {
  PENDING_APPROVAL: 'Awaiting approval',
  READY_FOR_DISPATCH: 'Ready for dispatch',
  PLACED: 'Placed',
  UNPAID: 'Unpaid',
  PENDING_REVIEW: 'In review',
  CHANGES_REQUESTED: 'Changes requested',
  RESOLVED_REFUND: 'Resolved with refund',
  RESOLVED_NO_REFUND: 'Resolved, no refund',
  SUBMITTED: 'Submitted',
};

export function StatusPill({ status, label, tone, className }: { status: string; label?: string; tone?: Tone; className?: string }) {
  const t = tone ?? STATUS_TONE[status] ?? 'neutral';
  return (
    <span className={cx('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[12px] font-medium ring-1 ring-inset whitespace-nowrap', tones[t], className)}>
      <span className={cx('size-1.5 rounded-full', dot[t])} aria-hidden />
      {label ?? STATUS_LABEL[status] ?? humanize(status)}
    </span>
  );
}

export function Tag({ children, tone = 'neutral' }: { children: ReactNode; tone?: Tone }) {
  return <span className={cx('inline-flex items-center rounded px-1.5 py-0.5 text-[12px] ring-1 ring-inset', tones[tone])}>{children}</span>;
}

/* ------------------------------------------------------------------- layout */
export function PageHeader({ title, subtitle, actions, back }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; back?: { to: string; label: string } }) {
  return (
    <header className="mb-6">
      {back && (
        <Link to={back.to} className="mb-2 inline-flex items-center gap-1 text-[13px] text-ink-3 hover:text-ink">
          <ChevronLeft className="size-4 rtl:rotate-180" aria-hidden /> {back.label}
        </Link>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="cond text-[26px] leading-tight font-semibold text-ink">{title}</h1>
          {subtitle && <div className="mt-1 text-ink-2">{subtitle}</div>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

export function Panel({ title, actions, children, className, padded = true, description }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; padded?: boolean; description?: ReactNode }) {
  return (
    <section className={cx('rounded-lg border border-line bg-surface', className)}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <div>
            {title && <h2 className="text-[15px] font-semibold">{title}</h2>}
            {description && <p className="text-[12.5px] text-ink-3">{description}</p>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={padded ? 'p-4' : ''}>{children}</div>
    </section>
  );
}

export function KeyValues({ items, cols = 2 }: { items: [ReactNode, ReactNode][]; cols?: 1 | 2 | 3 }) {
  return (
    <dl className={cx('grid gap-x-6 gap-y-3', cols === 1 ? 'grid-cols-1' : cols === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3')}>
      {items.map(([k, v], i) => (
        <div key={i} className="min-w-0">
          <dt className="text-[12.5px] text-ink-3">{k}</dt>
          <dd className="mt-0.5 break-words">{v ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      {icon && <div className="mb-3 text-ink-3 [&>svg]:size-8">{icon}</div>}
      <p className="font-medium">{title}</p>
      {children && <div className="mt-1 max-w-md text-ink-3">{children}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-ink-3" role="status">
      <Loader2 className="size-5 animate-spin" aria-hidden /> {label}…
    </div>
  );
}

export function ErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  const e = error as ApiError;
  return (
    <div className="flex items-start gap-2 rounded-md border border-chili/30 bg-chili-tint px-3 py-2.5 text-[13.5px] text-chili" role="alert">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="flex-1">
        {e.message ?? 'Something went wrong'}
        {e.details?.problems && (
          <ul className="mt-1 list-disc ps-5">
            {e.details.problems.map((p: any, i: number) => (
              <li key={i}>{p.message}</li>
            ))}
          </ul>
        )}
        {e.requestId && e.status >= 500 && <span className="block text-[12px] opacity-80">Request ID {e.requestId}</span>}
      </div>
      {onRetry && (
        <button className="underline" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

export function Notice({ tone = 'info', children, title }: { tone?: 'info' | 'waiting' | 'good'; children: ReactNode; title?: ReactNode }) {
  const t = { info: 'border-slate/25 bg-slate-tint text-slate', waiting: 'border-saffron/40 bg-saffron-tint text-saffron-ink', good: 'border-basil/30 bg-basil-tint text-basil-dark' }[tone];
  return (
    <div className={cx('flex gap-2.5 rounded-md border px-3.5 py-3 text-[13.5px]', t)}>
      <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div>
        {title && <p className="font-semibold">{title}</p>}
        {children}
      </div>
    </div>
  );
}

/** Wraps a query's loading / error / empty states consistently. */
export function QueryView<T>({ q, children, empty }: { q: { data?: T; isLoading: boolean; error: unknown; refetch: () => void }; children: (d: T) => ReactNode; empty?: (d: T) => ReactNode | null }) {
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorBanner error={q.error} onRetry={() => q.refetch()} />;
  if (q.data === undefined) return null;
  const e = empty?.(q.data);
  return <>{e ?? children(q.data)}</>;
}

/* -------------------------------------------------------------- pagination */
export function Pagination({ page, totalPages, total, onPage }: { page: number; totalPages: number; total: number; onPage: (p: number) => void }) {
  if (total === 0) return null;
  return (
    <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-2.5 text-[13px] text-ink-3">
      <span className="num">
        {total.toLocaleString()} result{total === 1 ? '' : 's'}
      </span>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page" icon={<ChevronLeft className="size-4 rtl:rotate-180" />} />
        <span className="num px-2">
          {page} / {totalPages}
        </span>
        <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => onPage(page + 1)} aria-label="Next page" icon={<ChevronRight className="size-4 rtl:rotate-180" />} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- modal */
export function Modal({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    setTimeout(() => ref.current?.querySelector<HTMLElement>('input,textarea,select,button[data-autofocus]')?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} role="dialog" aria-modal="true" className={cx('max-h-[92vh] w-full overflow-auto rounded-t-xl bg-surface shadow-xl sm:rounded-xl', wide ? 'sm:max-w-3xl' : 'sm:max-w-lg')}>
        <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
          <h2 className="text-base font-semibold">{title}</h2>
          <button className="rounded p-1 text-ink-3 hover:bg-sunken hover:text-ink" onClick={onClose} aria-label="Close">
            <X className="size-4" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-line bg-paper/60 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

/** Confirmation for consequential actions; optionally collects a reason (required when the backend needs one). */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  danger,
  reason,
  loading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  reason?: { label: string; required?: boolean; placeholder?: string };
  loading?: boolean;
  error?: unknown;
}) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (open) setText('');
  }, [open]);
  const blocked = !!reason?.required && text.trim().length < 3;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} disabled={blocked} loading={loading} onClick={() => onConfirm(text.trim())} data-autofocus>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {body && <div className="text-ink-2">{body}</div>}
        {reason && (
          <Field label={reason.label} required={reason.required}>
            {(id) => <Textarea id={id} value={text} onChange={(e) => setText(e.target.value)} placeholder={reason.placeholder} />}
          </Field>
        )}
        <ErrorBanner error={error} />
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------- toast */
interface ToastItem {
  id: number;
  message: string;
  tone: 'good' | 'bad';
}
const ToastCtx = createContext<(message: string, tone?: 'good' | 'bad') => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((message: string, tone: 'good' | 'bad' = 'good') => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, message, tone }]);
    setTimeout(() => setItems((x) => x.filter((i) => i.id !== id)), 4200);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={cx('pointer-events-auto flex items-center gap-2 rounded-md px-3.5 py-2.5 text-sm text-white shadow-lg', t.tone === 'good' ? 'bg-ink' : 'bg-chili')}>
            {t.tone === 'good' ? <Check className="size-4" /> : <AlertTriangle className="size-4" />}
            {t.message}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

/* -------------------------------------------------------------------- tabs */
export function Tabs<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: { value: T; label: ReactNode; count?: number }[] }) {
  return (
    <div className="flex gap-1 overflow-x-auto border-b border-line" role="tablist">
      {items.map((i) => (
        <button
          key={i.value}
          role="tab"
          aria-selected={value === i.value}
          onClick={() => onChange(i.value)}
          className={cx('-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13.5px] whitespace-nowrap', value === i.value ? 'border-basil font-medium text-ink' : 'border-transparent text-ink-3 hover:text-ink')}
        >
          {i.label}
          {i.count !== undefined && <span className="num rounded-full bg-sunken px-1.5 text-[11.5px] text-ink-2">{i.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** Filter bar row used above tables. */
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">{children}</div>;
}
